import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { 
  Anchor, Users, Megaphone, CheckCircle2, ArrowRight, Star, 
  ShoppingBag, BookOpen, Facebook, Instagram, Menu, X, 
  Navigation, Utensils, Wine, Flame, ExternalLink, 
  SmartphoneNfc, CreditCard, ArrowLeft, Loader2, Check, Music, Clock, Hotel,
  Calendar, Trophy, Compass
} from 'lucide-react';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyCbt-Lw6GvvdNfGLUUNuhf__rbxpReYnoQ",
  authDomain: "captain-key-west-hub.firebaseapp.com",
  projectId: "captain-key-west-hub",
  storageBucket: "captain-key-west-hub.firebasestorage.app",
  messagingSenderId: "1049588478957",
  appId: "1:1049588478957:web:add93fe371585c2c176de4"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const colors = {
  bg: 'bg-[#0a0f18]',
  card: 'bg-[#161e2b]',
  accent: 'bg-[#ff7e54]',
  textAccent: 'text-[#ff7e54]',
  border: 'border-slate-800'
};

// --- Nav Component (Reordered per request) ---
const Nav = ({ setView, view, mobileMenuOpen, setMobileMenuOpen }) => (
  <nav className={`fixed top-0 w-full z-50 border-b ${colors.border} ${colors.bg} px-6 py-4 flex justify-between items-center`}>
    <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView('home')}>
      <div className={`${colors.accent} p-2 rounded-xl`}>
        <Anchor className="text-white w-6 h-6" />
      </div>
      <div>
        <h1 className="text-white font-bold leading-none tracking-tight uppercase">Captain</h1>
        <p className="text-xs text-slate-400 font-medium tracking-widest uppercase leading-none">Key West</p>
      </div>
    </div>

    <div className="hidden lg:flex items-center gap-8 text-sm font-semibold text-slate-300">
      <button onClick={() => setView('advertising')} className={view === 'advertising' ? 'text-white' : 'hover:text-white transition-colors'}>Partner & Get Listed</button>
      <button onClick={() => setView('concierge')} className={view === 'concierge' ? 'text-white' : 'hover:text-white transition-colors'}>Business Portals</button>
      <button onClick={() => setView('agents')} className={view === 'agents' ? 'text-white' : 'hover:text-white transition-colors'}>Agent Program</button>
      <a href="https://app.captainkeywest.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 flex items-center gap-2 hover:opacity-80 transition-opacity font-bold">
        <SmartphoneNfc className="w-4 h-4" /> Open App
      </a>
    </div>

    <button className="lg:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
      {mobileMenuOpen ? <X /> : <Menu />}
    </button>

    {mobileMenuOpen && (
      <div className={`fixed inset-0 top-16 ${colors.bg} z-40 p-6 flex flex-col gap-6 lg:hidden animate-in fade-in slide-in-from-top-4`}>
        <button onClick={() => { setView('advertising'); setMobileMenuOpen(false); }} className="text-xl text-white font-bold text-left italic uppercase">Partner & Get Listed</button>
        <button onClick={() => { setView('concierge'); setMobileMenuOpen(false); }} className="text-xl text-white font-bold text-left italic uppercase">Business Portals</button>
        <button onClick={() => { setView('agents'); setMobileMenuOpen(false); }} className="text-xl text-white font-bold text-left italic uppercase">Agent Program</button>
        <div className="h-px bg-slate-800 w-full" />
        <a href="https://app.captainkeywest.com" className="text-xl text-emerald-400 font-bold italic uppercase flex items-center gap-2">
          <SmartphoneNfc className="w-5 h-5" /> Open App
        </a>
      </div>
    )}
  </nav>
);

const CategoryShowcase = () => (
  <section className="px-6 py-12 border-y border-slate-800/50 bg-slate-900/20">
    <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
      {[
        { label: "Charters", icon: <Navigation className="w-5 h-5" /> },
        { label: "Restaurants", icon: <Utensils className="w-5 h-5" /> },
        { label: "Bars", icon: <Wine className="w-5 h-5" /> },
        { label: "Live Music", icon: <Music className="w-5 h-5" /> },
        { label: "Happy Hours", icon: <Clock className="w-5 h-5" /> },
        { label: "Food Tours", icon: <Flame className="w-5 h-5" /> },
        { label: "Shops", icon: <ShoppingBag className="w-5 h-5" /> },
      ].map((cat, i) => (
        <div key={i} className="flex flex-col items-center gap-3 p-4 rounded-2xl border border-slate-800/50 group cursor-default">
          <div className="text-slate-500 group-hover:text-[#ff7e54] transition-colors">{cat.icon}</div>
          <span className="text-[10px] font-black text-slate-400 group-hover:text-white transition-colors uppercase tracking-widest text-center">{cat.label}</span>
        </div>
      ))}
    </div>
  </section>
);

// --- New App Preview Section (Bottom of Landing) ---
const AppPreviewSection = () => (
  <section className="relative w-full flex flex-col items-center pt-24 pb-0 overflow-hidden bg-gradient-to-b from-transparent to-black/40">
    <div className="max-w-6xl w-full flex justify-between items-center px-12 absolute bottom-60 z-30 hidden xl:flex">
      {/* Left Float */}
      <div className="flex flex-col items-start space-y-12">
        <div className="flex flex-col items-start space-y-3 animate-pulse">
          <div className="flex items-center gap-3 bg-slate-900/90 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-2xl">
            <div className="p-2 bg-[#ff7f50] rounded-lg text-white"><Calendar size={18} /></div>
            <span className="font-black italic uppercase text-xs tracking-tight">Instant Charters</span>
          </div>
          <p className="text-[10px] text-slate-400 italic ml-2 max-w-[150px]">Book sandbar trips instantly.</p>
        </div>
        <div className="flex flex-col items-start space-y-3 animate-pulse" style={{ animationDelay: '1s' }}>
          <div className="flex items-center gap-3 bg-slate-900/90 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-2xl">
            <div className="p-2 bg-[#34a4b8] rounded-lg text-white"><Utensils size={18} /></div>
            <span className="font-black italic uppercase text-xs tracking-tight">Island Flavors</span>
          </div>
          <p className="text-[10px] text-slate-400 italic ml-2 max-w-[150px]">Menus and local bites.</p>
        </div>
      </div>

      {/* Right Float */}
      <div className="flex flex-col items-end space-y-12">
        <div className="flex flex-col items-end space-y-3 animate-pulse" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center gap-3 bg-slate-900/90 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-2xl">
            <span className="font-black italic uppercase text-xs tracking-tight text-[#d97706]">Island Secrets</span>
            <div className="p-2 bg-[#d97706] rounded-lg text-white"><Trophy size={18} /></div>
          </div>
          <p className="text-[10px] text-slate-400 italic mr-2 text-right max-w-[150px]">Unlock hidden local rewards.</p>
        </div>
        <div className="flex flex-col items-end space-y-3 animate-pulse" style={{ animationDelay: '1.5s' }}>
          <div className="flex items-center gap-3 bg-slate-900/90 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-2xl">
            <span className="font-black italic uppercase text-xs tracking-tight">Captain's Journal</span>
            <div className="p-2 bg-emerald-500 rounded-lg text-white"><BookOpen size={18} /></div>
          </div>
          <p className="text-[10px] text-slate-400 italic mr-2 text-right max-w-[150px]">Curated island guides.</p>
        </div>
      </div>
    </div>

    <div className="relative z-20 mt-12">
      <div className="w-[300px] h-[600px] bg-black rounded-[3rem] border-[12px] border-slate-900 overflow-hidden shadow-[0_-20px_60px_rgba(0,0,0,0.6)] relative">
        <img 
          src="https://captainkeywest.com/wp-content/uploads/2026/04/Screenshot_20260428-000901.png" 
          className="w-full h-full object-cover" 
          alt="App Preview" 
        />
      </div>
    </div>

    <div className="mt-12 mb-20 z-30">
        <a href="https://app.captainkeywest.com" target="_blank" rel="noopener noreferrer" className="bg-[#ff7f50] text-white px-10 py-5 rounded-full font-black italic uppercase tracking-widest flex items-center gap-3 hover:scale-105 transition-transform shadow-xl">
            <Compass size={24} />
            Start Your Voyage
        </a>
    </div>
  </section>
);

const CheckoutView = ({ setView, isSuccess, handleSubmitSignup, formData, handleFormChange, isSubmitting, selectedPackage }) => {
  if (!selectedPackage) return <div className="pt-40 text-center text-white"><button onClick={() => setView('home')} className="text-[#ff7e54] underline">Return Home</button></div>;
  const isFree = selectedPackage.price === 'FREE';

  return (
    <div className="pt-32 pb-20 px-6 max-w-6xl mx-auto animate-in fade-in zoom-in-95">
      <button onClick={() => setView('home')} className="flex items-center gap-2 text-slate-500 hover:text-white mb-8 transition-colors group">
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back to Plans
      </button>

      {isSuccess ? (
        <div className="text-center py-20 bg-slate-900/50 rounded-[3rem] border border-emerald-500/30">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-3xl font-black text-white mb-4 uppercase italic">{isFree ? 'Update Received' : 'Partner Verified'}</h3>
          <p className="text-slate-400 max-w-md mx-auto">
            {isFree ? "We've received your business details and listing notes. We'll be in touch shortly!" : "Sending you to Stripe secure payment..."}
          </p>
          <div className="mt-8 flex justify-center">
            <Loader2 className="w-8 h-8 text-[#ff7e54] animate-spin" />
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <h3 className="text-3xl font-black text-white mb-8 uppercase italic tracking-tight">
              {isFree ? 'Partner &' : 'Complete Your'} <span className={colors.textAccent}>Get Listed</span>
            </h3>
            <form onSubmit={handleSubmitSignup} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Full Name</label>
                  <input required name="fullName" value={formData.fullName} onChange={handleFormChange} className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white focus:border-[#ff7e54] outline-none transition-all" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Email Address</label>
                  <input required type="email" name="email" value={formData.email} onChange={handleFormChange} className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white focus:border-[#ff7e54] outline-none transition-all" placeholder="john@example.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Business Name</label>
                  <input required name="businessName" value={formData.businessName} onChange={handleFormChange} className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white focus:border-[#ff7e54] outline-none transition-all" placeholder="Company Name" />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Phone Number</label>
                  <input required name="phone" value={formData.phone} onChange={handleFormChange} className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white focus:border-[#ff7e54] outline-none transition-all" placeholder="(305) 123-4567" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Listing Notes or Updates (Optional)</label>
                <textarea 
                  name="notes" 
                  value={formData.notes || ''} 
                  onChange={handleFormChange} 
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white focus:border-[#ff7e54] outline-none transition-all h-32 resize-none" 
                  placeholder="Need to update your menu, hours, or description? Tell us here..."
                />
              </div>

              {!isFree && (
                <div className="p-6 bg-slate-900/50 rounded-2xl border border-slate-800 flex items-center gap-4">
                  <div className="bg-[#ff7e54]/20 p-3 rounded-xl">
                    <CreditCard className="w-6 h-6 text-[#ff7e54]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white uppercase italic">Secure Stripe Checkout</p>
                    <p className="text-xs text-slate-500">You'll be redirected to pay after hitting the button below.</p>
                  </div>
                </div>
              )}

              <button disabled={isSubmitting} type="submit" className="w-full py-5 bg-[#ff7e54] text-white rounded-2xl font-black uppercase tracking-widest text-lg shadow-xl shadow-orange-500/20 hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-3">
                {isSubmitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <>{isFree ? 'Submit Listing Info' : 'Continue to Payment'}</>}
              </button>
            </form>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-slate-900 rounded-3xl p-8 border border-slate-800">
              <h4 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-6">Plan Summary</h4>
              <p className="text-xl font-bold text-white uppercase italic">{selectedPackage?.name || selectedPackage?.title}</p>
              <p className="text-4xl font-black text-white mt-4">
                {isFree ? 'FREE' : `$${selectedPackage?.price}`}
                {!isFree && <span className="text-xs text-slate-500 font-bold lowercase italic tracking-normal">/mo</span>}
              </p>
              <div className="h-px bg-slate-800 my-6" />
              <p className="text-xs text-slate-500 leading-relaxed italic">
                {isFree 
                  ? "We'll use this information to build or update your local listing on the Captain Key West app."
                  : "By proceeding, you agree to join the Captain Key West Partner Hub. Subscriptions are billed monthly."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem('ckw_current_view') || 'home');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({ fullName: '', email: '', businessName: '', phone: '', notes: '' });

  useEffect(() => {
    if (auth) {
      signInAnonymously(auth).catch(err => console.error("Auth error:", err));
      const unsubscribe = onAuthStateChanged(auth, setUser);
      return () => unsubscribe();
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.setItem('ckw_current_view', view);
    if (view !== 'checkout') { 
      setIsSuccess(false); 
      setIsSubmitting(false); 
    }
  }, [view]);

  const handleJoin = (pkg) => {
    setSelectedPackage(pkg);
    setView('checkout');
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmitSignup = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isFree = selectedPackage?.price === 'FREE';

    const redirectToNext = () => {
      if (!isFree && selectedPackage?.stripeUrl) {
        window.location.href = selectedPackage.stripeUrl;
      } else {
        setView('home');
      }
    };

    try {
      if (db) {
        await addDoc(collection(db, 'signups'), {
          ...formData,
          packageId: selectedPackage?.id,
          packageName: selectedPackage?.name || selectedPackage?.title,
          price: selectedPackage?.price,
          category: selectedPackage?.category,
          timestamp: serverTimestamp(),
          userId: user?.uid || 'anon',
          status: isFree ? 'active_free_listing' : 'pending_payment'
        });
      }
      setIsSuccess(true);
      setTimeout(redirectToNext, isFree ? 3500 : 1200);
    } catch (err) {
      console.error(err);
      redirectToNext();
    }
  };

  const Hero = () => (
    <section className="pt-32 pb-20 px-6 text-center max-w-5xl mx-auto">
      <div className="inline-flex items-center gap-2 bg-slate-900/50 border border-slate-700 rounded-full px-4 py-1.5 mb-8">
        <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
        <span className="text-xs font-bold text-slate-300 tracking-wider uppercase">Partner with Captain Key West</span>
      </div>
      <h2 className="text-4xl md:text-7xl font-black text-white mb-6 leading-tight uppercase italic tracking-tighter">
        Grow Your Business with the <span className={colors.textAccent}>Only All-In-One App</span> in Key West.
      </h2>
      <p className="text-lg text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed italic">
        Reach guests looking for Sunset Sails, Happy Hours, Live Music, and Sandbar Trips. We connect local Agents, Hotels, and Shops directly with the visitors who need them.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
        <button onClick={() => setView('advertising')} className={`${colors.accent} text-white px-8 py-5 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2`}><Megaphone className="w-5 h-5" /> Partner & Get Listed</button>
        <button onClick={() => setView('concierge')} className="bg-white text-black px-8 py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2"><Hotel className="w-5 h-5" /> Business Portals</button>
        <button onClick={() => setView('agents')} className={`${colors.card} border ${colors.border} text-white px-8 py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2`}><Users className="w-5 h-5" /> Agent Program</button>
      </div>
    </section>
  );

  const AgentSection = () => {
    const tier = { 
      id: 'agent_standard', name: "The Agent Plan", price: "45", category: "Agent Program", commission: "10%", featured: true,
      stripeUrl: 'https://buy.stripe.com/4gM9AS97a8okdU42H72oE09',
      features: [
        { title: "Custom Agent Profile Page", desc: "A dedicated landing page showcasing your local expertise." },
        { title: "Personalized QR Code Link", desc: "Your direct bridge from the real world to the digital app." },
        { title: "10% Booking Commission", desc: "Earn a flat 10% on activities booked through your profile." },
        { title: "Partner Hub Access", desc: "Track your referrals and manage your profile in real-time." }
      ]
    };

    return (
      <div className="pt-32 pb-20 px-6 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4">
        <div className="text-center mb-16">
          <h3 className="text-3xl md:text-6xl font-black text-white mb-4 italic uppercase tracking-tighter">AGENT <span className={colors.textAccent}>PROGRAM</span></h3>
          <p className="text-slate-400 text-lg italic">Earn referral commissions by recommending the island's best activities.</p>
        </div>
        <div className="max-w-xl mx-auto">
          <div className={`${colors.card} rounded-[3rem] p-10 border border-[#ff7e54] relative flex flex-col shadow-2xl`}>
            <h4 className="text-2xl font-black text-white mb-6 uppercase italic">{tier.name}</h4>
            <p className="text-4xl font-black text-white mb-8">${tier.price}<span className="text-xs text-slate-500 font-bold lowercase italic tracking-normal">/mo</span></p>
            <div className="bg-slate-900/50 p-4 rounded-2xl mb-8">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Commission Rate</p>
              <p className={`text-2xl font-black ${colors.textAccent}`}>{tier.commission}</p>
            </div>
            <div className="space-y-6 mb-10 flex-grow">
              {tier.features.map((f, i) => (
                <div key={i} className="flex gap-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-white leading-none mb-1.5">{f.title}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => handleJoin(tier)} className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest text-sm ${colors.accent} text-white shadow-lg shadow-orange-500/20`}>Sign Up & Start Earning</button>
          </div>
        </div>
      </div>
    );
  };

  const AdvertisingSection = () => {
    const packs = [
      { 
        id: 'ad_local', title: "Local Listing", price: "FREE", category: "Advertising", 
        stripeUrl: 'https://buy.stripe.com/9B628q2IM6gc8zKchH2oE06',
        btnText: 'Register Free Listing',
        subBtn: 'Get Listed or Update Listing',
        features: [
          { title: "Standard App Listing", desc: "Show up in your specific category." },
          { title: "Digital Hub Link", desc: "Send guests to your official site." },
          { title: "Staff Portal Add-on", desc: "Ability to add a commission portal for $75/mo." }
        ]
      },
      { 
        id: 'ad_featured', title: "Featured Partner", price: "125", category: "Advertising", featured: true, 
        stripeUrl: 'https://buy.stripe.com/00w00i6Z2340bLWftT2oE07',
        btnText: 'Partner & Boost Visibility',
        features: [
          { title: "Top 5 Placement", desc: "Stay at the top of your category." },
          { title: "Homepage Highlight", desc: "Featured spot on the app home screen." },
          { title: "Commission Portal", desc: "Includes Branded Staff Commission Portal." }
        ]
      },
      { 
        id: 'ad_authority', title: "Island Authority", price: "295", category: "Advertising", 
        stripeUrl: 'https://buy.stripe.com/7sY00i3MQeMI8zKchH2oE08',
        btnText: 'Secure the Top Spot',
        features: [
          { title: "Top 3 Guaranteed", desc: "Limited to 3 spots per category." },
          { title: "Priority Support", desc: "Direct line for listing updates and changes." },
          { title: "Commission Portal", desc: "Includes Branded Staff Commission Portal." }
        ]
      }
    ];
    return (
      <div className="pt-32 pb-20 px-6 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4">
        <div className="text-center mb-16">
          <h3 className="text-3xl md:text-6xl font-black text-white mb-4 italic uppercase tracking-tighter">PARTNER & <span className={colors.textAccent}>GET LISTED</span></h3>
          <p className="text-slate-400 text-lg italic text-center">Put your activity, shop, or restaurant at the top of the island's list.</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          {packs.map((p) => (
            <div key={p.id} className={`${colors.card} rounded-[3rem] p-10 border ${p.featured ? 'border-[#ff7e54]' : 'border-slate-800'} flex flex-col shadow-2xl relative`}>
              {p.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#ff7e54] text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
                  Most Popular
                </div>
              )}
              <h4 className="text-xl font-bold text-white mb-2 uppercase italic">{p.title}</h4>
              <p className="text-4xl font-black text-white mb-8">
                {p.price === 'FREE' ? 'FREE' : `$${p.price}`}
                {p.price !== 'FREE' && <span className="text-xs text-slate-500 font-bold lowercase italic tracking-normal">/mo</span>}
              </p>
              <div className="space-y-6 mb-10 flex-grow">
                {p.features.map((f, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#ff7e54] mt-2 shrink-0"></div>
                    <div>
                      <p className="text-sm font-bold text-slate-200 leading-none mb-1.5">{f.title}</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => handleJoin(p)} className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest text-sm ${p.featured ? colors.accent + ' text-white' : 'bg-slate-800 text-white border border-slate-700'}`}>
                {p.btnText}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ConciergeSection = () => {
    const options = [
      { 
        id: 'biz_portal', title: 'Business Commission Portal', price: '75', category: 'Portals',
        stripeUrl: 'https://buy.stripe.com/6oU7sK97a6gc17i4Pf2oE0a', 
        desc: "For Restaurants, Bars, and Retailers who want a branded portal for their staff to earn extra commissions.",
        features: [
            { title: "Staff Pool Earnings", desc: "A dedicated portal to pool money for your hard-working team." },
            { title: "Branded Portal", desc: "Custom signage and QR codes for your host stand or shop." },
            { title: "10% Commission", desc: "Earn 10% on every activity booked through your portal." }
        ]
      },
      { 
        id: 'hotel_portal', title: 'Hotel Concierge Portal', price: '450', category: 'Portals',
        stripeUrl: 'https://buy.stripe.com/eVqdR8gzCaws5nyftT2oE05',
        desc: "Full resort integration. Proprietary digital concierge for lobby/guest rooms with custom resort branding.",
        features: [
            { title: "Lobby QR Signage", desc: "Branded signage for your front desk and every guest room." },
            { title: "Full Guest Service", desc: "Instant access to bookings, island flavors, and live music." },
            { title: "10% Commission", desc: "Turn your front desk into a high-earning booking engine." }
        ]
      }
    ];

    return (
      <div className="pt-32 pb-20 px-6 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4">
        <div className="text-center mb-16">
          <h3 className="text-3xl md:text-6xl font-black text-white mb-4 italic uppercase tracking-tighter">BRANDED <span className={colors.textAccent}>BUSINESS PORTALS</span></h3>
          <p className="text-slate-400 text-lg italic">For Restaurants, Bars, and Hotels. Turn your staff into local experts.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {options.map((opt) => (
            <div key={opt.id} className={`${colors.card} border border-slate-800 rounded-[3rem] p-12 text-center shadow-2xl flex flex-col`}>
              <h4 className="text-2xl font-black text-white mb-4 uppercase italic">{opt.title}</h4>
              <p className="text-4xl font-black text-white mb-6">${opt.price}<span className="text-xs text-slate-500 font-bold lowercase italic tracking-normal">/mo</span></p>
              <p className="text-slate-400 mb-8 text-sm italic leading-relaxed">{opt.desc}</p>
              <div className="space-y-4 mb-10 text-left flex-grow">
                {opt.features.map((f, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-black text-white uppercase italic">{f.title}</p>
                      <p className="text-[10px] text-slate-500 leading-tight">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => handleJoin(opt)} className="bg-white text-black px-12 py-5 rounded-2xl font-black uppercase tracking-widest shadow-xl hover:bg-slate-100 transition-all">Get Portal Access</button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${colors.bg} font-sans text-slate-200 selection:bg-[#ff7e54]/30 overflow-x-hidden`}>
      <Nav setView={setView} view={view} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      {view === 'home' && (
        <main>
          <Hero />
          <CategoryShowcase />
          <section className="px-6 py-24 bg-gradient-to-b from-[#0a0f18] to-slate-900">
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
              <div className={`${colors.card} p-2 rounded-[3rem] border border-slate-700 relative overflow-hidden shadow-2xl`}>
                <img src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?q=80&w=2070&auto=format&fit=crop" className="rounded-[2.8rem] w-full aspect-[4/5] object-cover opacity-70" alt="Key West Nightlife" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-10 left-10 right-10 p-6">
                  <p className="text-white font-black text-2xl mb-2 italic uppercase">Join the Authority.</p>
                  <p className="text-slate-300 text-sm italic">The island's most trusted activity and hospitality network.</p>
                </div>
              </div>
              <div className="space-y-10">
                <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter leading-none">Reach the <span className={colors.textAccent}>Captive Audience.</span></h3>
                <div className="space-y-8">
                  {[
                    { title: "Complete Coverage", text: "Reach visitors looking for day charters, happy hours, dining, or shops.", icon: <Star className="w-5 h-5 text-[#ff7e54]" /> },
                    { title: "Direct Hub Link", text: "Hub links go directly to your reservation engine or website.", icon: <CheckCircle2 className="w-5 h-5 text-[#ff7e54]" /> },
                    { title: "The Captain's Journal", text: "Get featured in our curated journal of island guides.", icon: <BookOpen className="w-5 h-5 text-[#ff7e54]" /> }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-5">
                      <div className="bg-slate-800/80 p-4 rounded-2xl h-fit border border-slate-700">{item.icon}</div>
                      <div>
                        <h4 className="text-white text-lg font-black mb-1 italic uppercase tracking-tight">{item.title}</h4>
                        <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-4">
                  <button onClick={() => setView('advertising')} className="bg-[#ff7e54] text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg">
                    Partner & Get Listed <ArrowRight className="w-4 h-4" />
                  </button>
                  <a href="https://app.captainkeywest.com" target="_blank" rel="noopener noreferrer" className="text-center font-black uppercase italic tracking-widest text-emerald-400 hover:text-emerald-300 transition-colors">
                    Enter The Captain Key West App
                  </a>
                </div>
              </div>
            </div>
          </section>
          <AppPreviewSection />
        </main>
      )}

      {view === 'agents' && <AgentSection />}
      {view === 'advertising' && <AdvertisingSection />}
      {view === 'concierge' && <ConciergeSection />}
      {view === 'checkout' && (
        <CheckoutView 
          setView={setView}
          isSuccess={isSuccess}
          handleSubmitSignup={handleSubmitSignup}
          formData={formData}
          handleFormChange={handleFormChange}
          isSubmitting={isSubmitting}
          selectedPackage={selectedPackage}
        />
      )}

      <footer className="border-t border-slate-800 py-12 px-6 mt-20">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView('home')}>
            <Anchor className="text-[#ff7e54] w-6 h-6" />
            <span className="text-white font-black text-lg italic uppercase">Captain Key West</span>
          </div>
          <div className="flex-1 flex justify-center">
            <a href="https://captainkeywest.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 text-slate-300 px-6 py-3 rounded-2xl font-black text-xs uppercase hover:bg-slate-800 group transition-all">Visit Main Site <ExternalLink className="w-4 h-4" /></a>
          </div>
          <div className="flex gap-4">
            <Facebook className="w-5 h-5 text-slate-400 hover:text-[#ff7e54] cursor-pointer" />
            <Instagram className="w-5 h-5 text-slate-400 hover:text-[#ff7e54] cursor-pointer" />
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-12 pt-8 border-t border-slate-800/50 text-center">
          <p className="text-slate-600 text-[10px] font-bold uppercase tracking-widest">© {new Date().getFullYear()} Captain Key West. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
